import { Check, Search } from "lucide-react";
import type { JSX } from "react";
import { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  filterSpecialties,
  type ReferralSpecialtyKey,
  specialtyOptions,
} from "@/lib/referral-templates/specialties";
import { cn } from "@/lib/utils";
import { FIELD_INPUT } from "./styles";

interface Props {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  describedBy?: string;
}

// Specialty: type to search the list (in the interface language or English),
// or keep any other specialty or service as typed.
export const SpecialtyField = ({
  id,
  value,
  onChange,
  placeholder,
  invalid,
  describedBy,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const generated = useId();
  const inputId = id ?? generated;
  const listId = `${inputId}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const options = useMemo(
    () =>
      specialtyOptions((key: ReferralSpecialtyKey) =>
        t(`referral.specialties.${key}`),
      ),
    [t],
  );
  const shown = filterSpecialties(options, value).slice(0, 50);
  const exact = shown.some(
    (option) =>
      option.label.toLocaleLowerCase() === value.trim().toLocaleLowerCase(),
  );
  const custom = value.trim() && !exact ? value.trim() : null;
  const items = [
    ...(custom ? [{ key: "custom", label: custom, custom: true }] : []),
    ...shown.map((option) => ({ ...option, custom: false })),
  ];
  const choose = (label: string): void => {
    onChange(label);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-3.5 size-4 text-v3-text-3"
      />
      <input
        aria-activedescendant={
          open && items[active] ? `${listId}-${active}` : undefined
        }
        aria-autocomplete="list"
        aria-controls={listId}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        className={cn(FIELD_INPUT, "pl-9", invalid && "ring-2 ring-v3-rec")}
        data-testid="referral-specialty"
        id={inputId}
        placeholder={placeholder}
        role="combobox"
        type="text"
        value={value}
        onBlur={(event) => {
          // A pick in the list keeps focus here (its mousedown is
          // prevented); any other blur closes the list at once, without a
          // timer that could race a slow click.
          const next = event.relatedTarget as Node | null;
          if (next && listRef.current?.contains(next)) return;
          setOpen(false);
        }}
        onChange={(event) => {
          onChange(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onClick={() => {
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActive((index) => Math.min(index + 1, items.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === "Enter" && open && items[active]) {
            event.preventDefault();
            choose(items[active].label);
          } else if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
          }
        }}
      />
      {open && items.length > 0 && (
        <div
          className="absolute inset-x-0 top-12 z-20 max-h-64 overflow-y-auto rounded-xl bg-v3-panel p-1 shadow-v3-pop"
          id={listId}
          role="listbox"
          ref={listRef}
          onMouseDown={(event) => {
            // Keep focus in the field while picking.
            event.preventDefault();
          }}
        >
          {items.map((item, index) => (
            <div
              key={item.key}
              aria-selected={index === active}
              className={cn(
                "flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-3 text-[14px] text-v3-text sm:min-h-9",
                index === active && "bg-v3-surface-2",
              )}
              id={`${listId}-${index}`}
              role="option"
              tabIndex={-1}
              // Picked on press, so the choice never depends on the field
              // keeping focus until the click completes.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(item.label);
              }}
              onClick={() => {
                choose(item.label);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") choose(item.label);
              }}
              onMouseEnter={() => {
                setActive(index);
              }}
            >
              {item.custom ? (
                <span className="text-v3-text-2">
                  {t("referral.dialog.useTyped", { value: item.label })}
                </span>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.label === value && (
                    <Check aria-hidden className="size-4 text-v3-text" />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
